import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import AUHeader from '../components/AUHeader';
import {
  Container,
  Typography,
  Box,
  Button,
  TextField,
  RadioGroup,
  FormControlLabel,
  Radio,
  Paper,
  Stepper,
  Step,
  StepLabel,
  Alert,
  CircularProgress,
  Divider
} from '@mui/material';
import { CheckCircle } from '@mui/icons-material';
import {
  trackQualificationFormStep,
  trackQualificationFormAbandoned,
  trackQualificationSubmittedNotQualified,
  trackQualificationSegmentAssigned
} from '../utils/metaPixel';
import {
  ackQualificationMetaPixel,
  getEmailFromVideoAccess,
  trackInternalFunnelEvent
} from '../utils/funnelTracking';
import { getOrCreateAnonymousId } from '../utils/anonymousId';
import { getStoredVideoAccess, guardarContactoDelFormulario } from '../utils/videoAccessStorage';
import { getAttributionPayload } from '../utils/attribution';
import { corregirDominioTipeado } from '../utils/emailDominios';
import { trackVisit } from '../utils/trackVisit';

const FORM_STEP_KEYS = ['contact', 'qualification', 'price'];

// Las dos formas de entrar, con su precio. Vive acá y no suelto en el JSX porque
// el mismo dato se usa en las tarjetas de elección y en el recordatorio de la
// pregunta de inversión, y si se separan terminan diciendo montos distintos.
//
// `value` es el contrato con el backend: `computeInversion` más abajo, la
// segmentación en `qualificationFormController` y los mapas de etiquetas de
// `IncompletoEntry.js` dependen de estos strings exactos. Cambiar un `value`
// rompe las tres cosas y el panel empieza a mostrar códigos crudos.
const PLANES = [
  {
    value: 'agustin',
    nombre: 'Agustín Urzúa · Head Coach AU',
    precio: '$300.000 CLP / mes',
    detalle: 'Agustín te acompaña directamente: él revisa tus videos de ejecución, tus números y tu semana, sin intermediarios.',
  },
  {
    value: 'equipo_alvaro',
    // El nombre del team coach NO va acá (Agustín, 2026-10-23): la opción se
    // presenta por lo que es, no por quién la entrega. A quién le toca se
    // resuelve en `computeCoachElegido` y solo se usa internamente (columna del
    // Sheet, agenda y correo), nunca en esta pantalla.
    nombre: 'Team Coach AU',
    precio: '$150.000 CLP / mes',
    detalle: 'El mismo Método AU y la misma planificación. Un coach del equipo entrega tu seguimiento semanal, y Agustín supervisa y co-diseña tu proceso.',
  },
];

const FormPage = () => {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  // Corrección propuesta cuando el dominio del email parece un typo
  // ("@gmail.con"). Vacío mientras no haya nada que sugerir.
  const [emailSugerido, setEmailSugerido] = useState('');
  // Pantalla previa del paso 3: mensaje de Agustín antes de mostrar los planes
  const [showCoachIntro, setShowCoachIntro] = useState(true);

  const [formData, setFormData] = useState({
    nombre: '',
    email: '',
    whatsapp: '',
    instagram: '',
    experiencia: '',
    situacion: '',
    urgencia: '',
    frenoActual: '',
    coachPreference: '',
    coachPreferenceInicial: '',
    budgetConfirm: '',
    fallbackEquipo: '',
    inversion: '',
    videoMinute: null
  });
  const [formAccessOk, setFormAccessOk] = useState(false);
  const formStepPixelSentRef = useRef(new Set());
  const formProgressPostedRef = useRef(new Set());
  const activeStepRef = useRef(0);
  const submittedRef = useRef(false);

  // Pre-rellenar datos si vienen del localStorage (del formulario del video) y minuto del video.
  //
  // El formulario NO está bloqueado: quien llega desde un correo, desde otro
  // dispositivo o pasados los 30 días del TTL entra igual y escribe sus datos.
  // El gate anterior redirigía a "/" y no protegía nada: el paso 0 valida
  // nombre, email, WhatsApp e Instagram de todas formas, así que el localStorage
  // solo ahorra tipeo. Lo único que se pierde sin él es el minuto del video.
  useEffect(() => {
    const videoAccessData = getStoredVideoAccess();
    if (videoAccessData) {
      const data = videoAccessData;
      setFormData(prev => ({
        ...prev,
        nombre: data.nombre || '',
        email: data.email || '',
        whatsapp: data.whatsapp || '',
        instagram: data.instagram || '',
        videoMinute: data.videoMinute != null ? data.videoMinute : null
      }));
    }
    // Siempre en true: gatea el tracking por paso y el de abandono, que tienen
    // que correr también para quien entra directo.
    setFormAccessOk(true);
  }, []);

  // ── tracking visita ─────────────────────────────────────────────────────────
  //
  // Hasta el 2026-08-08 la home era la única página medida, y los correos
  // mandan el tráfico acá: quien llegaba desde un correo no quedaba registrado
  // en ninguna parte. Con la UTM del correo, esta visita es la que permite
  // saber qué campaña trae postulantes.
  useEffect(() => {
    trackVisit();
  }, []);

  useEffect(() => {
    activeStepRef.current = activeStep;
  }, [activeStep]);

  useEffect(() => {
    submittedRef.current = submitted;
  }, [submitted]);

  // Lo que se le manda al backend en cualquiera de los tres disparos. Se arma
  // desde un ref para que el pagehide no mande un estado viejo: cuando el
  // handler del pagehide corre, la clausura que capturó a formData puede ser
  // de hace varios renders.
  const formDataRef = useRef(formData);
  // ORDEN IMPORTA: este efecto tiene que quedar declarado ANTES del efecto de
  // "Pixel por paso + POST progreso" de más abajo. React corre los efectos en
  // orden de declaración dentro del mismo commit: si este se declarara
  // después, ese otro efecto leería formDataRef con el valor del render
  // anterior y el POST de cada paso guardaría respuestas viejas. No reordenar.
  useEffect(() => { formDataRef.current = formData; }, [formData]);

  // Arma el cuerpo del POST a partir de un estado explícito `d`, en vez de
  // leer formDataRef directo: así handleChange puede mandar el estado NUEVO
  // recién calculado (ver más abajo) y no uno viejo con un solo campo parchado.
  const armarProgresoDesde = useCallback((step, d) => ({
    step,
    nombre: d.nombre,
    email: d.email,
    whatsapp: d.whatsapp,
    instagram: d.instagram,
    experiencia: d.experiencia,
    situacion: d.situacion,
    urgencia: d.urgencia,
    frenoActual: d.frenoActual,
    coachPreference: d.coachPreference,
    budgetConfirm: d.budgetConfirm,
    fallbackEquipo: d.fallbackEquipo,
    videoMinute: d.videoMinute,
    anonymous_id: getOrCreateAnonymousId(),
    tracking: getAttributionPayload(),
  }), []);

  // useCallback con deps vacías: solo lee de formDataRef (estable) y de
  // funciones importadas (estables), así que la referencia no cambia entre
  // renders. Eso permite declararla como dependencia del useEffect de abajo
  // sin que se re-ejecute en cada render.
  const armarProgreso = useCallback(
    (step) => armarProgresoDesde(step, formDataRef.current),
    [armarProgresoDesde],
  );

  const postProgreso = useCallback((step) => {
    const backendUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
    fetch(`${backendUrl}/api/form-progress`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(armarProgreso(step)),
    }).catch(() => {});
  }, [armarProgreso]);

  // Pixel por paso (una vez por índice) + POST progreso al llegar a precio/inversión
  useEffect(() => {
    if (!formAccessOk) return;
    if (!formStepPixelSentRef.current.has(activeStep)) {
      formStepPixelSentRef.current.add(activeStep);
      trackQualificationFormStep(activeStep);
    }
    if (!formProgressPostedRef.current.has(activeStep)) {
      formProgressPostedRef.current.add(activeStep);
      postProgreso(activeStep);
    }
  }, [formAccessOk, activeStep, formData.nombre, formData.email, formData.whatsapp, formData.instagram, formData.videoMinute, postProgreso]);

  useEffect(() => {
    const onPageHide = () => {
      if (!formAccessOk || submittedRef.current) return;
      const idx = activeStepRef.current;
      trackQualificationFormAbandoned({ lastStepIndex: idx });
      const backendUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
      trackInternalFunnelEvent(
        backendUrl,
        'form_abandoned',
        {
          last_step_index: idx,
          last_step: FORM_STEP_KEYS[idx] ?? String(idx),
        },
        {
          email: getEmailFromVideoAccess(),
          anonymousId: getOrCreateAnonymousId(),
        }
      );
      // Un fetch se cancela cuando se cierra la pestaña; sendBeacon no. Es lo
      // que atrapa a quien se va a mitad de una pregunta. Va como Blob con tipo
      // JSON: por defecto sendBeacon manda text/plain y bodyParser.json no lo
      // parsearía.
      try {
        // soloParcial: true — este disparo puede ocurrir en CUALQUIER paso y
        // en cualquier momento (hasta por bfcache al cambiar de app en iOS),
        // así que nunca debe sumar una fila a "Avance Formulario" ni un
        // evento de funnel: ver el comentario en formProgressController.js.
        const cuerpo = new Blob(
          [JSON.stringify({ ...armarProgreso(idx), soloParcial: true })],
          { type: 'application/json' },
        );
        navigator.sendBeacon(`${backendUrl}/api/form-progress`, cuerpo);
      } catch (e) {
        // Un navegador sin sendBeacon no puede romper la salida de la página.
      }
    };
    window.addEventListener('pagehide', onPageHide);
    return () => window.removeEventListener('pagehide', onPageHide);
  }, [formAccessOk, armarProgreso]);

  const steps = ['Datos de Contacto', 'Sobre ti', 'Últimos detalles'];

  // Paso de la introducción a los planes. Se registra aparte porque entrar al
  // paso 3 no significa haber visto los precios: el paso abre con esta
  // introducción, y sin este evento no hay forma de saber si quien abandona se
  // fue al ver el monto o antes de llegar a verlo.
  const verLosPlanes = () => {
    setShowCoachIntro(false);
    const backendUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
    trackInternalFunnelEvent(backendUrl, 'plans_viewed', {}, {
      email: formData.email,
      anonymousId: getOrCreateAnonymousId(),
    });
  };

  // El plan que el lead marcó, para no repetir el monto en dos lugares del JSX.
  const planElegido = PLANES.find((plan) => plan.value === formData.coachPreference);

  const handleChange = (field) => (event) => {
    const valor = event.target.value;
    // Se calcula el estado NUEVO completo acá mismo (usando `formData` del
    // closure de este render, no formDataRef, que todavía no se actualizó) en
    // vez de parchar un solo campo sobre el estado viejo. Es lo que le
    // permite al POST de abajo mandar budgetConfirm/fallbackEquipo YA en ''
    // cuando el lead cambia de coach, en vez de arrastrar una respuesta que
    // la interfaz misma acaba de retractar.
    let nuevoEstado;
    if (field === 'coachPreference') {
      nuevoEstado = {
        ...formData,
        coachPreference: valor,
        // Solo se setea la primera vez. Si el lead cambia de opinión más
        // adelante (ej. eligió Agustín, vio el precio y se cambió a equipo),
        // queremos saber cuál fue la elección original.
        coachPreferenceInicial: formData.coachPreferenceInicial || valor,
        budgetConfirm: '',
        fallbackEquipo: '',
      };
    } else if (field === 'budgetConfirm') {
      nuevoEstado = { ...formData, budgetConfirm: valor, fallbackEquipo: '' };
    } else {
      nuevoEstado = { ...formData, [field]: valor };
    }
    setFormData(nuevoEstado);
    // La sugerencia era sobre el email anterior: si lo está reescribiendo, ya
    // no aplica.
    if (field === 'email') {
      setEmailSugerido('');
    }
    // Las respuestas del último paso se guardan APENAS se contestan: quien las
    // responde y se va nunca dispara otro POST, y es justo el lead que hay que
    // perseguir. Se arma desde `nuevoEstado`, no desde armarProgreso(), para
    // que el reset de budgetConfirm/fallbackEquipo al cambiar de coach viaje
    // en el mismo POST. soloParcial: true — ver comentario en
    // formProgressController.js sobre por qué este disparo no puede tocar
    // "Avance Formulario".
    if (['coachPreference', 'budgetConfirm', 'fallbackEquipo'].includes(field)) {
      const backendUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
      fetch(`${backendUrl}/api/form-progress`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...armarProgresoDesde(activeStepRef.current, nuevoEstado),
          soloParcial: true,
        }),
      }).catch(() => {});
    }
    setError('');
  };

  /** Reemplaza el email por la corrección propuesta. */
  const aplicarEmailSugerido = () => {
    setFormData(prev => ({ ...prev, email: emailSugerido }));
    setEmailSugerido('');
  };

  const computeInversion = () => {
    const { coachPreference, coachPreferenceInicial, budgetConfirm, fallbackEquipo } = formData;
    if (coachPreference === 'agustin') {
      if (budgetConfirm === 'si_listo') return 'agustin_listo';
      if (budgetConfirm === 'organizaria') return 'agustin_organizaria';
      if (budgetConfirm === 'no_posible') {
        if (fallbackEquipo === 'si') return 'agustin_a_equipo';
        // Sigue queriendo a Agustín, solo que más adelante: vale reactivarlo.
        if (fallbackEquipo === 'esperar') return 'agustin_espera';
        // Se baja de las dos opciones.
        if (fallbackEquipo === 'ninguna') return 'no_posible';
      }
    } else if (coachPreference === 'equipo_alvaro') {
      // Cambio de opinión: originalmente quiso Head Coach y terminó en equipo.
      // Cuenta igual que el camino explícito del fallback agustin → equipo.
      const cambioDeAgustin = coachPreferenceInicial === 'agustin';
      if (budgetConfirm === 'si_listo') return cambioDeAgustin ? 'agustin_a_equipo' : 'equipo_listo';
      if (budgetConfirm === 'organizaria') return cambioDeAgustin ? 'agustin_a_equipo' : 'equipo_organizaria';
      if (budgetConfirm === 'no_posible') return 'no_posible';
    }
    return 'no_posible';
  };

  // Nombre del team coach elegido (columna AB del Sheet + CC del email).
  // Vacío cuando el lead queda con Agustín o no eligió coach del equipo.
  const computeCoachElegido = () => {
    const { coachPreference, budgetConfirm, fallbackEquipo } = formData;
    if (coachPreference === 'equipo_alvaro') return 'Alvaro Niklitschek';
    // Álvaro es hoy el único Team Coach con cupos, así que el fallback no
    // pregunta con quién: se asigna directo.
    if (coachPreference === 'agustin' && budgetConfirm === 'no_posible' && fallbackEquipo === 'si') {
      return 'Alvaro Niklitschek';
    }
    return '';
  };

  const validateStep = (step) => {
    switch (step) {
      case 0:
        if (!formData.nombre.trim() || !formData.email.trim() || !formData.whatsapp.trim() || !formData.instagram.trim()) {
          setError('Por favor completa todos los campos de contacto');
          return false;
        }
        if (!/\S+@\S+\.\S+/.test(formData.email)) {
          setError('Por favor ingresa un email válido');
          return false;
        }
        // Dominio que parece mal tipeado ("@gmail.con"): se muestra la
        // corrección y se frena el paso UNA vez. Si el lead insiste con el
        // mismo correo, avanza igual — el heurístico puede equivocarse con un
        // dominio raro pero real, y trabarlo acá cuesta más que el typo.
        {
          const sugerencia = corregirDominioTipeado(formData.email);
          if (sugerencia && sugerencia !== emailSugerido) {
            setEmailSugerido(sugerencia);
            return false;
          }
        }
        return true;
      case 1:
        if (!formData.experiencia || !formData.situacion || !formData.urgencia || !formData.frenoActual.trim()) {
          setError('Por favor responde todas las preguntas de cualificación');
          return false;
        }
        return true;
      case 2:
        if (!formData.coachPreference) {
          setError('Por favor selecciona con quién te interesa trabajar');
          return false;
        }
        if (!formData.budgetConfirm) {
          setError('Por favor responde la pregunta de inversión');
          return false;
        }
        if (formData.coachPreference === 'agustin' && formData.budgetConfirm === 'no_posible' && !formData.fallbackEquipo) {
          setError('Por favor indica si te interesa la opción del equipo AU');
          return false;
        }
        return true;
      default:
        return true;
    }
  };

  const handleNext = () => {
    if (validateStep(activeStep)) {
      setActiveStep((prevStep) => prevStep + 1);
      setError('');
    }
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
    setError('');
  };

  const handleSubmit = async () => {
    if (!validateStep(activeStep)) {
      return;
    }

    setLoading(true);
    setError('');

    try {
      const backendUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
      const response = await fetch(`${backendUrl}/api/qualification-form`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          inversion: computeInversion(),
          coachElegido: computeCoachElegido(),
          anonymous_id: getOrCreateAnonymousId(),
          tracking: getAttributionPayload(),
        })
      });

      if (!response.ok) {
        throw new Error('Error al enviar el formulario');
      }

      const result = await response.json();
      const cualificado = result?.cualificado;
      const segment = result?.segment || 'unknown';
      const score = result?.score ?? null;
      const isQualified =
        cualificado === true ||
        cualificado === 'true' ||
        cualificado === 1 ||
        cualificado === '1';

      trackQualificationSegmentAssigned({ segment, score });

      // Los datos de contacto del formulario mandan sobre los que quedaron guardados
      // al desbloquear el video: el lead pudo corregirlos en el paso 1. Si no se
      // sincroniza, /qualified le manda a Meta el email viejo (hash que no matchea con
      // nadie) y el ack de la columna P busca una fila que no existe.
      // Además, quien llega directo a /form (link por correo, o pasaron los 30 días
      // del TTL) no tiene ningún registro guardado todavía, así que acá hay que
      // CREARLO y no solo actualizarlo: si no, /qualified se queda sin email que
      // mandarle a Meta por CAPI.
      guardarContactoDelFormulario({
        nombre: formData.nombre,
        email: formData.email,
        whatsapp: formData.whatsapp,
        instagram: formData.instagram,
      });

      if (isQualified) {
        // /qualified muestra la agenda del coach elegido (sin dato: Agustín)
        localStorage.setItem('au_agenda_coach', computeCoachElegido() || 'agustin');
        navigate('/qualified');
        return;
      } else {
        trackQualificationSubmittedNotQualified({ segment });
        ackQualificationMetaPixel(backendUrl, formData.email, false);
      }

      setSubmitted(true);
    } catch (error) {
      console.error('Error:', error);
      setError('Hubo un error al enviar el formulario. Por favor intenta nuevamente.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <Box>
        <AUHeader />

        <Container maxWidth="md" sx={{ py: 8 }}>
          <Paper elevation={3} sx={{ p: 6, textAlign: 'center', borderRadius: 3 }}>
            <CheckCircle sx={{ fontSize: 80, color: 'success.main', mb: 3 }} />
            <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 700 }}>
              ¡Formulario Enviado Exitosamente!
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
              Gracias por tu interés en AU Coaching Services. Revisaremos tu información y nos pondremos en contacto contigo muy pronto.
            </Typography>
            <Divider sx={{ my: 3 }} />
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              ¿Qué sigue?
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              • Revisaremos tu aplicación en las próximas 24-48 horas
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              • Te contactaremos por WhatsApp o email
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
              • Si tu perfil es adecuado, agendaremos una reunión inicial
            </Typography>
            <Button
              variant="contained"
              size="large"
              onClick={() => navigate('/')}
              sx={{ mt: 2 }}
            >
              Volver al Inicio
            </Button>
          </Paper>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ bgcolor: 'grey.50', minHeight: '100vh' }}>
      {/* Header */}
      <AUHeader />

      {/* Contenido del Formulario */}
      <Container maxWidth="md" sx={{ py: 6 }}>
        <Paper elevation={3} sx={{ p: { xs: 3, md: 5 }, borderRadius: 3 }}>
          <Box sx={{ mb: 4 }}>
            <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 700, textAlign: 'center' }}>
              FORMULARIO DE APLICACIÓN
            </Typography>
            <Typography variant="h6" component="h2" sx={{ textAlign: 'center', color: 'text.secondary', mb: 3 }}>
              AU Coaching Services
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
              Este formulario nos permite evaluar si eres un buen fit para AU Coaching.
            </Typography>
          </Box>

          <Stepper activeStep={activeStep} sx={{ mb: 5 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {/* Step 0: Datos de Contacto */}
          {activeStep === 0 && (
            <Box>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
                1️⃣ Datos de Contacto
              </Typography>
              <TextField
                fullWidth
                label="Nombre Completo"
                value={formData.nombre}
                onChange={handleChange('nombre')}
                margin="normal"
                required
              />
              <TextField
                fullWidth
                label="Instagram (usuario)"
                value={formData.instagram}
                onChange={handleChange('instagram')}
                margin="normal"
                placeholder="@tuusuario"
                required
              />
              <TextField
                fullWidth
                label="WhatsApp (con código de país)"
                value={formData.whatsapp}
                onChange={handleChange('whatsapp')}
                margin="normal"
                placeholder="+56912345678"
                required
              />
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={formData.email}
                onChange={handleChange('email')}
                margin="normal"
                required
              />
              {emailSugerido && (
                <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
                  ¿Quisiste decir{' '}
                  <Box
                    component="span"
                    onClick={aplicarEmailSugerido}
                    sx={{
                      color: 'primary.main',
                      fontWeight: 600,
                      cursor: 'pointer',
                      textDecoration: 'underline',
                    }}
                  >
                    {emailSugerido}
                  </Box>
                  ? Si tu correo está bien, vuelve a apretar continuar.
                </Typography>
              )}
              {/* Consentimiento explícito para contactar por WhatsApp. Además de
                  ser lo correcto, es el requisito que piden las plataformas de
                  mensajería para autorizar el envío a estos números. */}
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 2, fontSize: '0.85rem', lineHeight: 1.5 }}
              >
                Al continuar aceptas que te contactemos por WhatsApp y email
                para coordinar tu reunión inicial y darle seguimiento a tu
                postulación.
              </Typography>
            </Box>
          )}

          {/* Step 1: Preguntas de Cualificación */}
          {activeStep === 1 && (
            <Box>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
                2️⃣ Sobre ti
              </Typography>

              {/* Pregunta 1 */}
              <Box sx={{ mb: 4 }}>
                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                  1. ¿Hace cuánto entrenas de forma constante en el gimnasio?
                </Typography>
                <RadioGroup value={formData.experiencia} onChange={handleChange('experiencia')}>
                  <FormControlLabel 
                    value="menos-6-meses" 
                    control={<Radio />} 
                    label="Menos de 6 meses" 
                  />
                  <FormControlLabel 
                    value="6-meses-2-años" 
                    control={<Radio />} 
                    label="Entre 6 meses y 2 años" 
                  />
                  <FormControlLabel 
                    value="mas-2-años" 
                    control={<Radio />} 
                    label="Más de 2 años" 
                  />
                </RadioGroup>
              </Box>

              {/* Pregunta 2 */}
              <Box sx={{ mb: 4 }}>
                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                  2. ¿Cuál es tu situación actual?
                </Typography>
                <RadioGroup value={formData.situacion} onChange={handleChange('situacion')}>
                  <FormControlLabel 
                    value="no-refleja-esfuerzo" 
                    control={<Radio />} 
                    label="Entreno hace tiempo pero mi físico no refleja el esfuerzo que hago" 
                  />
                  <FormControlLabel 
                    value="estancado" 
                    control={<Radio />} 
                    label="Siento que estoy estancado y no sé qué variable ajustar" 
                  />
                  <FormControlLabel 
                    value="nivel-competitivo" 
                    control={<Radio />} 
                    label="Quiero llevar mi físico a un nivel competitivo / avanzado" 
                  />
                  <FormControlLabel 
                    value="rutina-nueva" 
                    control={<Radio />} 
                    label="Solo estoy buscando una rutina nueva" 
                  />
                </RadioGroup>
              </Box>

              {/* Pregunta 3 */}
              <Box sx={{ mb: 4 }}>
                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                  3. ¿Qué tan urgente es para ti empezar a cambiar esto?
                </Typography>
                <RadioGroup value={formData.urgencia} onChange={handleChange('urgencia')}>
                  <FormControlLabel value="necesito-ahora"   control={<Radio />} label="Quiero empezar ahora" />
                  <FormControlLabel value="proximas-semanas" control={<Radio />} label="Quiero empezar pronto" />
                  <FormControlLabel value="sin-prisa"        control={<Radio />} label="No tengo interés de comenzar de inmediato" />
                  <FormControlLabel value="no-interesado"    control={<Radio />} label="No estoy interesado" />
                </RadioGroup>
              </Box>

              {/* Pregunta 4 */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                  4. ¿Qué sientes que te está frenando hoy para lograr resultados reales?
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  placeholder="Cuéntame brevemente qué estás intentando resolver"
                  value={formData.frenoActual}
                  onChange={handleChange('frenoActual')}
                  required
                />
              </Box>
            </Box>
          )}

          {/* Step 2 — intro: por qué este seguimiento es distinto, antes de mostrar los planes */}
          {activeStep === 2 && showCoachIntro && (
            <Box>
              <Paper elevation={0} sx={{ bgcolor: '#EBF3FF', p: { xs: 2.5, md: 3.5 }, borderRadius: 2, border: '1px solid rgba(26,127,255,0.2)' }}>
                <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.7 }}>
                  <strong>En simple:</strong> la alternativa de siempre es un nutricionista y un personal trainer por separado. El nutricionista te entrega una minuta y, con suerte, te ve una vez al mes para motivarte con los números de una medición que suele ser poco precisa. El personal trainer te cuenta las repeticiones. Plan sin seguimiento cercano y entrenamiento sin programación: por eso la gente se estanca.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.7 }}>
                  Acá va todo junto y con seguimiento cercano de verdad. Cada semana revisamos tu semana completa: videos de ejecución, números de entrenamiento, dieta, sueño, pasos y cómo se mueve el peso. Con esa data el entrenamiento y la nutrición se ajustan de inmediato, no al ojo ni a fin de mes.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.7 }}>
                  Todo dentro de la app, con check-ins respondidos en cápsulas de video: tu progreso físico, tu ejecución y los cambios necesarios para seguir progresando. El plan no solo se entrega: se cumple, se ejecuta de manera correcta y se corrige rápido. Esa es la diferencia entre volver a estancarte o llegar al mejor físico de tu vida.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, lineHeight: 1.7 }}>
                  Si esto es lo que buscas, revisa los planes y elige con quién quieres trabajar.
                </Typography>
              </Paper>
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                <Button variant="contained" onClick={verLosPlanes} sx={{ px: 5, py: 1.5 }}>
                  Ver los planes
                </Button>
              </Box>
            </Box>
          )}

          {/* Step 2: Últimos detalles */}
          {activeStep === 2 && !showCoachIntro && (
            <Box>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
                3️⃣ Últimos detalles
              </Typography>

              {/* El método va ARRIBA y es común a las dos opciones: el valor se
                  establece antes de que aparezca ningún precio. Antes este bloque
                  se mostraba DESPUÉS de elegir coach, así que el lead elegía sin
                  saber cuánto costaba y el monto le llegaba de golpe. */}
              <Paper elevation={0} sx={{ bgcolor: 'grey.50', p: 2.5, mb: 3, borderRadius: 2 }}>
                <Typography variant="body2" sx={{ fontWeight: 700, mb: 1.5 }}>
                  El Método AU incluye:
                </Typography>
                <Box component="ul" sx={{ pl: 2, mb: 0, mt: 0 }}>
                  {[
                    'Programación integral periodizada: siempre sabes en qué fase estás y qué busca.',
                    'Entrenamiento de sobrecarga progresiva: un programa que avanza semana a semana, no una rutina.',
                    'Técnica revisada en video: las cargas suben según cómo ejecutas.',
                    'Nutrición adaptada a tu día a día: aprendes a sostenerla aunque no sea perfecta.',
                    'Check-in semanal con cápsula de video, fotos de progreso y análisis de tu data (peso, sueño, pasos, adherencia).',
                  ].map((item, i) => (
                    <Typography key={i} component="li" variant="body2" sx={{ mb: 0.75, lineHeight: 1.6 }}>
                      {item}
                    </Typography>
                  ))}
                </Box>
                <Typography variant="body1" sx={{ mt: 1.5, fontWeight: 700, color: '#1A7FFF' }}>
                  Un camino claro para salir del estancamiento y enfocarte solo en lo que importa.
                </Typography>
              </Paper>

              {/* Las dos formas de entrar, SIN precio todavía.
                  Medido el 2026-10-23: mostrar los dos montos acá, antes de que el
                  lead eligiera, partió la conversión del formulario a la mitad (de
                  ~22 formularios por semana a ~10, con el mismo tráfico entrando).
                  De 330 que llegaron a este paso y lo abandonaron, 291 se fueron
                  SIN marcar ninguna opción. Elegir primero y ver el monto después
                  es lo que daba 22-27%: el lead ya eligió con quién quiere trabajar
                  cuando aparece el precio. No vuelvas a poner el monto acá. */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, mb: 0.5 }}>
                  ¿Con quién te interesa trabajar?
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.6 }}>
                  El método es el mismo en las dos. Lo que cambia es quién revisa tu semana.
                </Typography>
                <RadioGroup value={formData.coachPreference} onChange={handleChange('coachPreference')}>
                  {PLANES.map((plan) => {
                    const elegido = formData.coachPreference === plan.value;
                    return (
                      <Paper
                        key={plan.value}
                        elevation={0}
                        sx={{
                          p: 2,
                          mb: 1.5,
                          borderRadius: 2,
                          border: '1px solid',
                          borderColor: elegido ? '#1A7FFF' : 'grey.300',
                          bgcolor: elegido ? '#EBF3FF' : 'transparent',
                        }}
                      >
                        <FormControlLabel
                          value={plan.value}
                          control={<Radio />}
                          sx={{ alignItems: 'flex-start', m: 0, '& .MuiRadio-root': { pt: 0 } }}
                          label={(
                            <Box>
                              <Typography variant="body1" sx={{ fontWeight: 700, lineHeight: 1.4, mb: 0.5 }}>
                                {plan.nombre}
                              </Typography>
                              <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                                {plan.detalle}
                              </Typography>
                            </Box>
                          )}
                        />
                      </Paper>
                    );
                  })}
                </RadioGroup>
              </Box>

              {/* El monto aparece acá, DESPUÉS de que el lead eligió con quién
                  quiere trabajar. Ver arriba por qué no va antes. */}
              {formData.coachPreference && (
                <Box sx={{ mb: 2 }}>
                  <Paper elevation={0} sx={{ bgcolor: 'grey.50', p: 2.5, mb: 2, borderRadius: 2 }}>
                    <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
                      Elegiste <strong>{planElegido?.nombre}</strong>: <strong>{planElegido?.precio}</strong>.
                    </Typography>
                  </Paper>

                  {/* Quien elige Agustín tiene que enterarse de que existe la otra
                      opción ANTES de irse, y sin tener que declarar primero que no
                      puede pagar (Agustín, 2026-10-23). El fallback de más abajo
                      solo aparece si responde "hoy no es el momento", y de los 39
                      que eligieron coach y abandonaron, 28 nunca respondieron esa
                      pregunta: nunca lo veían. */}
                  {formData.coachPreference === 'agustin' && (
                    <Paper
                      elevation={0}
                      sx={{ bgcolor: '#EBF3FF', p: 2.5, mb: 2, borderRadius: 2, border: '1px solid rgba(26,127,255,0.2)' }}
                    >
                      <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
                        También puedes entrar con el <strong>Team Coach AU</strong> por{' '}
                        <strong>$150.000 CLP / mes</strong>: el mismo método y la misma
                        planificación, con Agustín supervisando y co-diseñando tu proceso.
                      </Typography>
                      <Typography variant="body1" sx={{ lineHeight: 1.7, mt: 1.5 }}>
                        Si prefieres esa modalidad, márcala más arriba.
                      </Typography>
                    </Paper>
                  )}

                  <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, mb: 1.5 }}>
                    ¿Puedes hacer esta inversión actualmente?
                  </Typography>
                  <RadioGroup value={formData.budgetConfirm} onChange={handleChange('budgetConfirm')}>
                    <FormControlLabel
                      value="si_listo"
                      control={<Radio />}
                      label="Sí, tengo acceso a ese presupuesto y estoy listo/a si esto se alinea con lo que busco"
                      sx={{ mb: 1 }}
                    />
                    <FormControlLabel
                      value="organizaria"
                      control={<Radio />}
                      label="Si este proceso me convence, vería cómo organizar mis recursos para hacerlo posible"
                      sx={{ mb: 1 }}
                    />
                    <FormControlLabel
                      value="no_posible"
                      control={<Radio />}
                      label="Hoy no es el momento para mí económicamente"
                    />
                  </RadioGroup>
                </Box>
              )}

              {/* Fallback: solo si eligió Agustín y no puede pagar $300k */}
              {formData.coachPreference === 'agustin' && formData.budgetConfirm === 'no_posible' && (
                <Paper elevation={0} sx={{ bgcolor: '#EBF3FF', p: 2.5, borderRadius: 2, border: '1px solid rgba(26,127,255,0.2)' }}>
                  <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.7 }}>
                    ¿Te interesa entrar con el <strong>Team Coach AU</strong> por
                    <strong> $150.000 CLP / mes</strong>? Mismo método y misma planificación,
                    con Agustín supervisando y co-diseñando tu proceso.
                  </Typography>
                  {/* El lead ya declaró que hoy no puede invertir, así que la alternativa no es
                      una preferencia de coach sino de momento. Se separan los dos casos: el que
                      sigue queriendo entrar con Agustín más adelante (reactivable) y el que se baja. */}
                  <RadioGroup value={formData.fallbackEquipo} onChange={handleChange('fallbackEquipo')}>
                    <FormControlLabel
                      value="si"
                      control={<Radio />}
                      label="Sí, me interesa esa opción"
                      sx={{ mb: 1 }}
                    />
                    <FormControlLabel
                      value="esperar"
                      control={<Radio />}
                      label="Prefiero esperar a un mejor momento económico para trabajar con Agustín"
                      sx={{ mb: 1 }}
                    />
                    <FormControlLabel
                      value="ninguna"
                      control={<Radio />}
                      label="Por ahora prefiero no tomar ninguna de las dos"
                    />
                  </RadioGroup>
                  {formData.fallbackEquipo === 'si' && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 2, lineHeight: 1.6 }}>
                      Tu proceso quedaría con un <strong>Team Coach AU</strong>, supervisado
                      por Agustín.
                    </Typography>
                  )}
                </Paper>
              )}
            </Box>
          )}

          {/* Botones de navegación (ocultos mientras se muestra la intro del paso 3) */}
          {!(activeStep === 2 && showCoachIntro) && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 5 }}>
            <Button
              disabled={activeStep === 0}
              onClick={handleBack}
              sx={{ px: 4 }}
            >
              Atrás
            </Button>
            <Box sx={{ flex: '1 1 auto' }} />
            {activeStep === steps.length - 1 ? (
              <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={loading}
                sx={{ px: 4, py: 1.5 }}
              >
                {loading ? <CircularProgress size={24} /> : 'Enviar Formulario'}
              </Button>
            ) : (
              <Button
                variant="contained"
                onClick={handleNext}
                sx={{ px: 4, py: 1.5 }}
              >
                Siguiente
              </Button>
            )}
          </Box>
          )}
        </Paper>
      </Container>
    </Box>
  );
};

export default FormPage;
